/**
 * The single-line diagram's edits (the API's SldOverrides) as a reducer: label texts, offsets, hidden optional
 * elements and extra boxes, keyed by the diagram's stable element ids (app/design/permits/sld.py), plus the orphaned
 * edits (an element that is gone) to re-attach or discard. Pure; tested in sldOverrides.test.ts.
 */

export type ExtraKind = "sub_panel" | "battery" | "generator" | "ev_charger" | "custom";

export interface SldExtra {
  id: string;
  kind: ExtraKind;
  name: string;
  detail?: string | null;
  connect_to: string;
}

export interface Offset {
  dx: number;
  dy: number;
}

export interface Overrides {
  labels: Record<string, string>;
  offsets: Record<string, Offset>;
  hidden: string[];
  extras: SldExtra[];
}

export const EMPTY: Overrides = { labels: {}, offsets: {}, hidden: [], extras: [] };
export const MAX_EXTRAS = 12;
const LIMIT = 1000;

export const EXTRA_KINDS: { kind: ExtraKind; label: string; name: string; connect: string }[] = [
  { kind: "sub_panel", label: "Sub-panel", name: "Sub-panel", connect: "msp" },
  { kind: "battery", label: "Battery", name: "Battery", connect: "msp" },
  { kind: "generator", label: "Generator", name: "Generator", connect: "msp" },
  { kind: "ev_charger", label: "EV charger", name: "EV charger", connect: "msp" },
  { kind: "custom", label: "Custom box", name: "Equipment", connect: "msp" },
];

/** The API's document (fields may be null) as a full Overrides. */
export function normalize(o: Partial<Record<keyof Overrides, unknown>> | null | undefined): Overrides {
  const src = o ?? {};
  return {
    labels: { ...((src.labels as Record<string, string> | null) ?? {}) },
    offsets: { ...((src.offsets as Record<string, Offset> | null) ?? {}) },
    hidden: [...((src.hidden as string[] | null) ?? [])],
    extras: [...((src.extras as SldExtra[] | null) ?? [])],
  };
}

export type OverridesAction =
  | { type: "replace"; overrides: Overrides }
  | { type: "label"; id: string; text: string; generated?: string }
  | { type: "resetLabel"; id: string }
  | { type: "move"; id: string; dx: number; dy: number }
  | { type: "setOffset"; id: string; dx: number; dy: number }
  | { type: "resetOffset"; id: string }
  | { type: "hide"; id: string; hidden: boolean }
  | { type: "addExtra"; kind: ExtraKind; connect_to: string; name?: string; id?: string }
  | { type: "updateExtra"; id: string; patch: Partial<Omit<SldExtra, "id">> }
  | { type: "removeExtra"; id: string }
  | { type: "resetElement"; id: string }
  | { type: "discardOrphan"; key: string }
  | { type: "reattachOrphan"; key: string; to: string }
  | { type: "resetAll" };

const clamp = (v: number) => Math.max(-LIMIT, Math.min(LIMIT, Math.round(v * 10) / 10));

function without<T>(record: Record<string, T>, key: string): Record<string, T> {
  if (!(key in record)) return record;
  const next = { ...record };
  delete next[key];
  return next;
}

function withOffset(o: Overrides, id: string, dx: number, dy: number): Overrides {
  const x = clamp(dx);
  const y = clamp(dy);
  return { ...o, offsets: x === 0 && y === 0 ? without(o.offsets, id) : { ...o.offsets, [id]: { dx: x, dy: y } } };
}

/** An id for a new extra box: its kind and the first free number ("battery-1"), within the API's pattern. */
export function nextExtraId(o: Overrides, kind: ExtraKind): string {
  const base = kind.replace(/_/g, "-");
  const taken = new Set(o.extras.map((e) => e.id));
  for (let n = 1; ; n += 1) if (!taken.has(`${base}-${n}`)) return `${base}-${n}`;
}

export function parseOrphan(key: string): { kind: "labels" | "offsets" | "hidden" | "extras"; id: string } | null {
  const m = /^(labels|offsets|hidden|extras)\.(.+)$/.exec(key);
  return m ? { kind: m[1] as "labels" | "offsets" | "hidden" | "extras", id: m[2]! } : null;
}

export function reduceOverrides(o: Overrides, a: OverridesAction): Overrides {
  switch (a.type) {
    case "replace":
      return normalize(a.overrides);
    case "label": {
      const text = a.text.replace(/\r/g, "").slice(0, 300);
      if (!text.trim() || (a.generated != null && text === a.generated)) return { ...o, labels: without(o.labels, a.id) };
      return { ...o, labels: { ...o.labels, [a.id]: text } };
    }
    case "resetLabel":
      return { ...o, labels: without(o.labels, a.id) };
    case "move": {
      const cur = o.offsets[a.id] ?? { dx: 0, dy: 0 };
      return withOffset(o, a.id, cur.dx + a.dx, cur.dy + a.dy);
    }
    case "setOffset":
      return withOffset(o, a.id, a.dx, a.dy);
    case "resetOffset":
      return { ...o, offsets: without(o.offsets, a.id) };
    case "hide": {
      const has = o.hidden.includes(a.id);
      if (a.hidden === has) return o;
      return { ...o, hidden: a.hidden ? [...o.hidden, a.id] : o.hidden.filter((h) => h !== a.id) };
    }
    case "addExtra": {
      if (o.extras.length >= MAX_EXTRAS) return o;
      const kind = EXTRA_KINDS.find((k) => k.kind === a.kind)!;
      const id = a.id ?? nextExtraId(o, a.kind);
      return { ...o, extras: [...o.extras, { id, kind: a.kind, name: (a.name ?? kind.name).slice(0, 60), detail: null, connect_to: a.connect_to }] };
    }
    case "updateExtra":
      return {
        ...o,
        extras: o.extras.map((e) =>
          e.id === a.id ? { ...e, ...a.patch, name: (a.patch.name ?? e.name).slice(0, 60), detail: a.patch.detail === undefined ? e.detail : a.patch.detail ? a.patch.detail.slice(0, 120) : null } : e,
        ),
      };
    case "removeExtra": {
      const el = `extra.${a.id}`;
      return { ...o, extras: o.extras.filter((e) => e.id !== a.id), labels: without(o.labels, el), offsets: without(o.offsets, el), hidden: o.hidden.filter((h) => h !== el) };
    }
    case "resetElement":
      return { ...o, labels: without(o.labels, a.id), offsets: without(o.offsets, a.id), hidden: o.hidden.filter((h) => h !== a.id) };
    case "discardOrphan": {
      const p = parseOrphan(a.key);
      if (!p) return o;
      if (p.kind === "labels") return { ...o, labels: without(o.labels, p.id) };
      if (p.kind === "offsets") return { ...o, offsets: without(o.offsets, p.id) };
      if (p.kind === "hidden") return { ...o, hidden: o.hidden.filter((h) => h !== p.id) };
      return reduceOverrides(o, { type: "removeExtra", id: p.id });
    }
    case "reattachOrphan": {
      const p = parseOrphan(a.key);
      if (!p || !a.to) return o;
      if (p.kind === "extras") return { ...o, extras: o.extras.map((e) => (e.id === p.id ? { ...e, connect_to: a.to } : e)) };
      if (p.kind === "labels") {
        const text = o.labels[p.id];
        return text == null ? o : { ...o, labels: { ...without(o.labels, p.id), [a.to]: text } };
      }
      if (p.kind === "offsets") {
        const off = o.offsets[p.id];
        return off == null ? o : { ...o, offsets: { ...without(o.offsets, p.id), [a.to]: off } };
      }
      return { ...o, hidden: [...o.hidden.filter((h) => h !== p.id && h !== a.to), a.to] };
    }
    case "resetAll":
      return { labels: {}, offsets: {}, hidden: [], extras: [] };
  }
}

/** How many manual edits there are ("3 manual edits"). */
export function editCount(o: Overrides): number {
  return Object.keys(o.labels).length + Object.keys(o.offsets).length + o.hidden.length + o.extras.length;
}

export function isEdited(o: Overrides, id: string): boolean {
  return id in o.labels || id in o.offsets || o.hidden.includes(id);
}

/** A stable string of the document, to compare what is shown with what was saved. */
export function signature(o: Overrides): string {
  const sorted = <T,>(r: Record<string, T>) => Object.keys(r).sort().map((k) => [k, r[k]]);
  return JSON.stringify([sorted(o.labels), sorted(o.offsets), [...o.hidden].sort(), o.extras]);
}

/** How far an element must still be moved on screen: its offset now minus the one the drawing was rendered with. */
export function offsetDelta(local: Overrides, rendered: Overrides, id: string): Offset {
  const a = local.offsets[id] ?? { dx: 0, dy: 0 };
  const b = rendered.offsets[id] ?? { dx: 0, dy: 0 };
  return { dx: a.dx - b.dx, dy: a.dy - b.dy };
}

const NAMES: Record<string, string> = {
  title: "Title",
  subtitle: "Subtitle (system size)",
  "array.label": "Array caption",
  "array.more": "“+ n more” note",
  inverter: "Inverter",
  combiner: "AC combiner panel",
  ac_disconnect: "AC disconnect",
  msp: "Main service panel",
  meter: "Utility meter (symbol)",
  "meter.label": "Meter caption",
  "meter.sub": "Meter sub-caption",
  "utility.label": "Utility name",
  "wire.array.label": "Wire label (array → inverter)",
  "wire.ac.label": "Wire label (AC)",
  "note.rapid_shutdown": "Note: rapid shutdown",
  "note.temperatures": "Note: design temperatures",
  "note.conductors": "Note: conductors",
  "legend.dc": "Legend: DC",
  "legend.ac": "Legend: AC",
  "note.grounding": "Note: grounding",
  "note.code": "Note: code edition",
  "msp.gec": "Grounding electrode caption",
  meter_main: "Meter-main outline",
  "meter_main.label": "Meter-main caption",
  "tap.label": "Supply-side tap caption",
};

/** A person's name for a diagram element id ("array.S2" → "String S2", "extra.battery-1" → its box's name). */
export function elementName(id: string, extras: SldExtra[] = []): string {
  if (NAMES[id]) return NAMES[id]!;
  const array = /^array\.([SB])(\d+)$/.exec(id);
  if (array) return `${array[1] === "S" ? "String" : "Branch"} ${array[1]}${array[2]}`;
  const inverter = /^inverter\.(\d+)$/.exec(id);
  if (inverter) return `Inverter ${inverter[1]}`;
  if (id.startsWith("extra.")) {
    const e = extras.find((x) => `extra.${x.id}` === id);
    return e ? e.name : "Added box";
  }
  return id;
}

/** Where an extra box may connect, by node id. */
export function nodeName(node: string): string {
  return node === "array" ? "Array" : elementName(node);
}
